import { call, select, takeLatest } from 'redux-saga/effects';
import * as notificationsApi from '@api/notifications';
import * as subscriptionsApi from '@api/subscriptions';
import { actions } from './clinicalTrialSubscription.slice';
import {
  FetchAllSubscriptionsForTypeAction,
  FetchNotificationCountsAction,
  FetchSubscriptionsAction,
  DeleteSubscriptionAction,
  UpdateSubscriptionAction,
  EditSubscriptionByIdAction,
  DeleteSubscriptionByIdAction,
  CreateSubscriptionAction,
} from './clinicalTrialSubscription.types';
import { Resp, dispatch } from '../helpers';
import { NotificationTypes } from '@dynamika-shared/types/subscription';
import { PaginationAndSortType } from '../paginationAndSort/paginationAndSort.types';
import { RootState } from '../store';
import { runToast } from '../tools';
import { AxiosError } from 'axios';

export function transformNotificationCountsToRecord(
  response: Resp<typeof notificationsApi.fetchNotificationCounts>
) {
  return response.reduce((acc, item) => {
    acc[item.notificationType] = item.count;
    return acc;
  }, {} as Record<typeof response[number]['notificationType'], number>);
}

export function* fetchNotificationCountsWorker(
  action: FetchNotificationCountsAction
) {
  const { clinicalTrialId } = action.payload;
  try {
    const response: Resp<typeof notificationsApi.fetchNotificationCounts> = yield call(
      notificationsApi.fetchNotificationCounts,
      clinicalTrialId
    );
    
    const notificationCounts: Partial<Record<NotificationTypes, number>> = yield call(transformNotificationCountsToRecord, response);
    
    yield dispatch(actions.fetchNotificationCountsSuccess(notificationCounts));
  } catch (error) {
    yield dispatch(actions.fetchNotificationCountsFailure(error as Error));
  }
}

const ALL_SUBSCRIPTIONS_PAGE_SIZE = 1000;

export function* fetchAllSubscriptionsForTypeWorker(
  action: FetchAllSubscriptionsForTypeAction
) {
  const { clinicalTrialId, notificationType } = action.payload;
  try {
    const response: Resp<typeof subscriptionsApi.fetchSubscriptions> =
      yield call(
        subscriptionsApi.fetchSubscriptions,
        clinicalTrialId,
        notificationType,
        { page: 1, size: ALL_SUBSCRIPTIONS_PAGE_SIZE }
      );
    yield dispatch(
      actions.fetchAllSubscriptionsForTypeSuccess(response.content)
    );
  } catch {
    yield dispatch(actions.fetchAllSubscriptionsForTypeFailure());
  }
}

export function* fetchSubscriptionsWorker(action: FetchSubscriptionsAction) {
  const { sort, search, scope, type, notificationType, clinicalTrialId } = action.payload;
  try {
    const pagSort = (yield select(
      (state: RootState) =>
        state.paginationAndSort?.lists?.subscriptions || {
          currentPage: 1,
          currentPageSize: 10,
          sortBy: sort,
        }
    )) as PaginationAndSortType;
    const sortBy = sort ?? pagSort.sortBy;
    const response: Resp<typeof subscriptionsApi.fetchSubscriptions> = yield call(
      subscriptionsApi.fetchSubscriptions,
      clinicalTrialId,
      notificationType,
      {
        page: pagSort.currentPage,
        size: pagSort.currentPageSize,
        sort: sortBy,
        search,
        scope,
        type,
      }
    );
    yield dispatch(actions.fetchSubscriptionsSuccess(response));
  } catch (error) {
    yield dispatch(actions.fetchSubscriptionsFailure(error as Error));
    yield call(runToast, 'error', 'Error during fetching subscriptions');
  }
}

export function* updateSubscriptionWorker(action: UpdateSubscriptionAction) {
  const { subscriptionIds, clinicalTrialId } = action.payload;
  try {
    yield call(
      subscriptionsApi.updateSubscription,
      subscriptionIds,
      clinicalTrialId
    );
    yield dispatch(actions.updateSubscriptionSuccess({
      subscriptionIds,
    }));
    yield dispatch(actions.fetchNotificationCounts({ clinicalTrialId }));
  } catch (error) {
    yield dispatch(actions.updateSubscriptionFailure(error as Error));
    yield call(runToast, 'error', 'Error during updating subscription');
  }
}

export function* deleteSubscriptionWorker(action: DeleteSubscriptionAction) {
  const { subscriptionIds, clinicalTrialId } = action.payload;
  try {
    yield call(
      subscriptionsApi.deleteSubscription,
      subscriptionIds,
      clinicalTrialId
    );
    yield dispatch(
      actions.deleteSubscriptionSuccess({
        subscriptionIds,
      }));
    yield dispatch(actions.fetchNotificationCounts({ clinicalTrialId }));
  } catch (error) {
    yield dispatch(actions.deleteSubscriptionFailure(error as Error));
    yield call(runToast, 'error', 'Error during deleting subscription');
  }
}

export function* createSubscriptionWorker(action: CreateSubscriptionAction) {
  const { clinicalTrialId, body } = action.payload;
  try {
    const response: Resp<typeof subscriptionsApi.createSubscription> = yield call(subscriptionsApi.createSubscription, clinicalTrialId, body);
    yield call(runToast, 'success', 'Subscription created successfully');
    yield dispatch(actions.createSubscriptionSuccess(
    {
      subscription: response,
    }
    ));
    yield dispatch(actions.fetchNotificationCounts({ clinicalTrialId }));
  } catch (error) {
    if (error instanceof AxiosError) {
      yield call(runToast, 'error', error.response?.data.title ?? 'Error during creating subscription');
    } else {
      yield call(runToast, 'error', 'Error during creating subscription');
    }
    yield dispatch(actions.createSubscriptionFailure(error as Error));
  }
}

export function* deleteSubscriptionByIdWorker(action: DeleteSubscriptionByIdAction) {
  const { subscriptionId, clinicalTrialId } = action.payload;
  try {
    yield call(subscriptionsApi.deleteSubscriptionById, subscriptionId, clinicalTrialId);
    yield call(runToast, 'success', 'Subscription deleted successfully');
    yield dispatch(actions.deleteSubscriptionByIdSuccess());
    yield dispatch(actions.fetchNotificationCounts({ clinicalTrialId }));
  } catch (error) {
    if (error instanceof AxiosError) {
      yield call(runToast, 'error', error.response?.data.title ?? 'Error during deleting subscription');
    } else {
      yield call(runToast, 'error', 'Error during deleting subscription');
    }
    yield dispatch(actions.deleteSubscriptionByIdFailure(error as Error));
  }
}

export function* editSubscriptionByIdWorker(action: EditSubscriptionByIdAction) {
  const { subscriptionId, clinicalTrialId, body } = action.payload;
  try {
    yield call(subscriptionsApi.editSubscriptionById, subscriptionId, clinicalTrialId, body);
    yield call(runToast, 'success', 'Subscription edited successfully');
    yield dispatch(actions.editSubscriptionByIdSuccess());
    yield dispatch(actions.fetchNotificationCounts({ clinicalTrialId }));
  } catch (error) {
    if (error instanceof AxiosError) {
      yield call(runToast, 'error', error.response?.data.title ?? 'Error during editing subscription');
    } else {
      yield call(runToast, 'error', 'Error during editing subscription');
    }
    yield dispatch(actions.editSubscriptionByIdFailure(error as Error));
  }
}

export function* clinicalTrialSubscriptionSaga() {
  yield takeLatest(actions.fetchNotificationCounts.type, fetchNotificationCountsWorker);
  yield takeLatest(actions.fetchSubscriptions.type, fetchSubscriptionsWorker);
  yield takeLatest(
    actions.fetchAllSubscriptionsForType.type,
    fetchAllSubscriptionsForTypeWorker
  );
  yield takeLatest(actions.updateSubscription.type, updateSubscriptionWorker);
  yield takeLatest(actions.deleteSubscription.type, deleteSubscriptionWorker);
  yield takeLatest(actions.createSubscription.type, createSubscriptionWorker);
  yield takeLatest(actions.deleteSubscriptionById.type, deleteSubscriptionByIdWorker);
  yield takeLatest(actions.editSubscriptionById.type, editSubscriptionByIdWorker);
} 